<template>
  <v-container fluid :class="['print-container', {printing: isPrinting}]">
    <v-toolbar flat class="d-print-none mb-2" color="white">
      <ProductionSelector
        :dates="dates"
        :production="production"
        v-on:update:dates="dates=$event"
        v-on:update:production="production=$event"
      />
      <v-spacer/>
      <span class="caption grey--text ml-4 mr-2">Plating line:</span>
      <v-btn-toggle v-model="filterPlatingLine" mandatory dense>
        <v-btn value="both" small>Both</v-btn>
        <v-btn value="main" small>Main</v-btn>
        <v-btn value="allergy" small>Allergy</v-btn>
      </v-btn-toggle>
      <v-spacer/>
      <v-checkbox
        v-model="forceSticker"
        label="Force Sticker"
        dense
        hide-details
        class="mx-2"
      />
    </v-toolbar>

    <v-progress-linear indeterminate v-if="loading"/>

    <v-card class="d-print-none mb-3" v-if="orders.length > 0 || loading">
      <v-card-title>
        Find Labels
        <v-spacer/>
        <v-btn color="secondary" @click="clearFilters">New Search</v-btn>
      </v-card-title>
      <v-card-text class="pt-0">
        <p class="body-2 mb-3 d-print-none">
          Use the filters below to narrow down orders, then check the rows you want to print.
          Use the checkbox in the header to select or deselect all visible results, then click
          <strong>Generate PDF</strong> to download the labels.
          Use <strong>List (as shown)</strong> to print in the current sort order, or <strong>List (bin search)</strong> to automatically sort by bin before printing.
        </p>
        <v-row dense>
          <v-col cols="12" sm="3">
            <v-combobox
              v-model="filterMeal"
              label="Meal name"
              :items="mealNameItems"
              clearable
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="1">
            <v-select
              v-model="filterTod"
              label="Meal type"
              :items="todItems"
              clearable
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="2">
            <v-select
              v-model="filterOrderStream"
              label="Label stream"
              :items="orderStreamItems"
              clearable
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="2">
            <v-select
              v-model="filterStream"
              label="Base stream"
              :items="streamItems"
              clearable
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="2">
            <v-text-field
              v-model="inputCustomer"
              label="Customer name"
              clearable
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="1">
            <v-select
              v-model="filterSize"
              label="Size"
              :items="sizeItems"
              clearable
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="1">
            <v-text-field
              v-model="inputBin"
              label="Bin"
              clearable
              dense
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card-text v-if="!loading && orders.length > 0 && !hasFilters" class="text-center grey--text">
      Enter search criteria above to find labels.
    </v-card-text>

    <v-card v-if="!loading && hasFilters && ordersFiltered.length > 0" flat>
      <v-card-actions class="d-print-none">
        <v-checkbox
          :input-value="allSelected"
          :indeterminate="someSelected && !allSelected"
          :label="`Select all (${ordersFiltered.length})`"
          dense
          hide-details
          class="mt-0 mr-4"
          @change="toggleAll"
        />
        <v-spacer/>
        <v-btn
          color="primary"
          :disabled="selectedOrders.length === 0 || generatingPDF"
          :loading="generatingPDF"
          @click="downloadPDF"
        >
          Generate PDF ({{ selectedOrders.length }} labels)
        </v-btn>
        <v-btn class="ml-2" outlined @click="print()">
          <v-icon left>mdi-printer</v-icon>
          List (as shown)
        </v-btn>
        <v-btn class="ml-2" outlined @click="printByBin()">
          <v-icon left>mdi-printer</v-icon>
          List (bin search)
        </v-btn>
        <v-spacer/>
        <span class="caption grey--text">{{ ordersFiltered.length }} results</span>
      </v-card-actions>

      <v-data-table
        class="d-print-none"
        :headers="headers"
        :items="ordersFiltered"
        :items-per-page="-1"
        item-key="_key"
        hide-default-footer
        dense
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @current-items="printItems = $event"
      >
        <template v-slot:item.selected="{item}">
          <v-checkbox v-model="item.selected" dense hide-details/>
        </template>
      </v-data-table>

      <div class="d-none d-print-block print-filter-summary">
        <table class="print-filter-table">
          <tr>
            <template v-if="filterMeal"><td class="print-filter-label">Meal:</td><td class="print-filter-value">{{ filterMeal }}</td></template>
            <template v-if="filterTod"><td class="print-filter-label">Meal type:</td><td class="print-filter-value">{{ filterTod }}</td></template>
            <template v-if="filterCustomer"><td class="print-filter-label">Customer:</td><td class="print-filter-value">{{ filterCustomer }}</td></template>
            <template v-if="filterBin"><td class="print-filter-label">Bin:</td><td class="print-filter-value">{{ filterBin }}</td></template>
            <template v-if="filterOrderStream"><td class="print-filter-label">Label stream:</td><td class="print-filter-value">{{ filterOrderStream }}</td></template>
            <template v-if="filterStream"><td class="print-filter-label">Base stream:</td><td class="print-filter-value">{{ filterStream }}</td></template>
            <template v-if="filterSize"><td class="print-filter-label">Size:</td><td class="print-filter-value">{{ filterSize }}</td></template>
            <template v-if="filterPlatingLine && filterPlatingLine !== 'both'"><td class="print-filter-label">Plating line:</td><td class="print-filter-value">{{ filterPlatingLine }}</td></template>
          </tr>
        </table>
      </div>
      <p class="d-none d-print-block print-sort-note">
        <b>Note:</b>
        This list can be sorted — ask if you want a different order, ie sorted by bin if you have to check bins
      </p>
      <table class="d-none d-print-table print-table">
        <thead>
          <tr>
            <th class="print-col-check"></th>
            <th class="print-col-bin">Bin</th>
            <th class="print-col-meal">Meal</th>
            <th class="print-col-stream">Stream</th>
            <th class="print-col-customer">Customer</th>
            <th class="print-col-size">Size</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="order in printItems" :key="order._key">
            <td class="print-col-check"><span class="print-checkbox"></span></td>
            <td class="print-col-bin">{{ order.bin }}</td>
            <td class="print-col-meal"><span class="print-ellipsis">{{ order.mealName }}</span></td>
            <td class="print-col-stream">{{ combinedStream(order) }}</td>
            <td class="print-col-customer">{{ order.customerName }}</td>
            <td class="print-col-size">{{ order.meal_size }}</td>
          </tr>
        </tbody>
      </table>
    </v-card>

    <v-card-text v-if="!loading && orders.length === 0 && dates.length > 0 && production">
      No orders found.
    </v-card-text>
  </v-container>
</template>

<script>
// "no pork" → "pork" (fed-lib restrictions-client restrictionLabel)
const withoutNo = (text) => String(text || '').replace(/\([^)]*\)/g, '').replace(/^no\s+/i, '').replace(/\s+/g, ' ').trim();
import {mapActions, mapGetters} from 'vuex';
import api from '@/api';
import urlState from '@/router/urlState';
import ProductionSelector from '@/components/ProductionSelector.vue';
import {capitalise, getBestBefore, getFullDay, shortenBin} from '@/store/utils';
import debounce from 'lodash/debounce';

function sortOrders(orders) {
  return orders.slice().sort((a, b) => {
    if (a.isMultiplePersonMeal && !b.isMultiplePersonMeal) return -1;
    if (!a.isMultiplePersonMeal && b.isMultiplePersonMeal) return 1;
    if (a.isMultiplePersonMeal && b.isMultiplePersonMeal) {
      return a.isMultiplePersonMeal.localeCompare(b.isMultiplePersonMeal);
    }
    if (a.streamName !== b.streamName) {
      return a.streamName.localeCompare(b.streamName);
    }
    const aName = `${a.mealName}-${a.meal_id}`;
    const bName = `${b.mealName}-${b.meal_id}`;
    if (aName !== bName) return aName.localeCompare(bName);
    if ((a.meal && a.meal.tod) !== (b.meal && b.meal.tod)) {
      return (a.meal && a.meal.tod) - (b.meal && b.meal.tod);
    }
    if (a.meal_size !== b.meal_size) {
      return b.meal_size.localeCompare(a.meal_size); // reverse: s < m < l
    }
    return a.customerName.localeCompare(b.customerName);
  });
}

export default {
  name: 'LabelSearch',
  components: {ProductionSelector},
  mixins: [urlState],
  data() {
    return {
      dates: [],
      production: null,
      inputCustomer: '',
      inputBin: '',
      filterMeal: '',
      filterCustomer: '',
      filterBin: '',
      filterOrderStream: null,
      filterStream: null,
      filterSize: null,
      filterTod: null,
      filterPlatingLine: 'both',
      forceSticker: false,
      loading: false,
      orders: [],
      printItems: [],
      generatingPDF: false,
      isPrinting: false,
      sortBy: null,
      sortDesc: false,
    };
  },
  created() {
    this.$watch('inputCustomer', debounce(function (v) { this.filterCustomer = v; }, 300));
    this.$watch('inputBin', debounce(function (v) { this.filterBin = v; }, 300));
  },
  mounted() {
    window.addEventListener('beforeprint', this.onBeforePrint);
    window.addEventListener('afterprint', this.onAfterPrint);
    this.syncToUrl({param: 'dates', urlParam: 'dates', initFromRoute: true, parseCallback: (v) => Array.isArray(v) ? v : [v]});
    this.syncToUrl({param: 'production', urlParam: 'production', initFromRoute: true});
    this.syncToUrl({param: 'filterMeal', urlParam: 'meal', initFromRoute: true});
    this.syncToUrl({param: 'filterCustomer', urlParam: 'customer', initFromRoute: true});
    this.syncToUrl({param: 'filterBin', urlParam: 'bin', initFromRoute: true});
    this.syncToUrl({param: 'filterOrderStream', urlParam: 'order_stream', initFromRoute: true});
    this.syncToUrl({param: 'filterStream', urlParam: 'base_stream', initFromRoute: true});
    this.syncToUrl({param: 'filterSize', urlParam: 'size', initFromRoute: true});
    this.syncToUrl({param: 'filterTod', urlParam: 'tod', initFromRoute: true});
    this.syncToUrl({param: 'filterPlatingLine', urlParam: 'plating_line', initFromRoute: true});
    this.syncToUrl({param: 'forceSticker', urlParam: 'sticker', initFromRoute: true, parseCallback: (v) => v === 'true'});
  },
  watch: {
    dates() { this.fetchData(); },
    production() { this.fetchData(); },
    filterCustomer(v) { if (v !== this.inputCustomer) this.inputCustomer = v; },
    filterBin(v) { if (v !== this.inputBin) this.inputBin = v; },
  },
  beforeDestroy() {
    window.removeEventListener('beforeprint', this.onBeforePrint);
    window.removeEventListener('afterprint', this.onAfterPrint);
  },
  methods: {
    ...mapActions(['fetchMeal', 'fetchDiets', 'fetchStreams', 'fetchRestrictions']),
    onBeforePrint() { this.isPrinting = true; },
    onAfterPrint() { this.isPrinting = false; },
    print() { window.print(); },
    async printByBin() {
      const prevSortBy = this.sortBy;
      const prevSortDesc = this.sortDesc;
      this.sortBy = 'bin';
      this.sortDesc = false;
      await this.$nextTick();
      window.print();
      let restored = false;
      const restore = () => {
        if (restored) return;
        restored = true;
        this.sortBy = prevSortBy;
        this.sortDesc = prevSortDesc;
        window.removeEventListener('focus', restore);
      };
      window.addEventListener('afterprint', restore, {once: true});
      window.addEventListener('focus', restore);
    },
    async fetchData() {
      if (!this.dates || this.dates.length === 0 || !this.production) return;
      this.loading = true;
      this.orders = [];
      try {
        const sorted = [...this.dates].sort();
        const from = sorted[0];
        const to = sorted[sorted.length - 1];
        const rawOrders = await api.get('v2/order/search', {
          params: {from, to, productions: [this.production]}
        }).then(({data}) => data);

        const mealIds = [...new Set(rawOrders.map(o => o.meal_id).filter(Boolean))];
        await Promise.all([
          ...mealIds.map(mealId => {
            // a meals-by-date fetch may have overwritten the cached meal with a
            // summary row that has no ingredients — force a full re-fetch
            const cached = this.$store.state.meal[mealId];
            const force = !cached || !cached.ingredients;
            return this.fetchMeal({id: mealId, force}).catch(() => {});
          }),
          this.fetchDiets(),
          this.fetchStreams(),
          this.fetchRestrictions(),
        ]);

        const streams = this.streams || {};
        rawOrders.forEach((o, index) => {
          o._key = index;
          o.meal = this.getMeal(o.meal_id) || {name: `meal ${o.meal_id}`};
          o.streamName = (streams[o.stream] || {name: o.stream || ''}).name;
          o.orderStreamName = (streams[o.order_stream] || {name: o.order_stream || ''}).name;
          o.mealName = o.meal.name;
          o.customerName = `${o.first_name} ${o.last_name}`;
          o.isMultiplePersonMeal = o.meal.name && o.meal.name.match(/\d+ person/i) ? o.meal.name : null;
          o.selected = false;
          // allergy chips on the label come from this map - see Labels.vue
          // (`allergyWarningIds` are delivered as-is: "contains …" when found)
          const containsRestriction = (id) => !!(o.allergyWarnings && o.allergyWarnings[id]);
          o.restrictionTagsText = new Map(
            [...(o.allergies || []), ...(o.allergyWarningIds || [])]
              .map(a => [a, containsRestriction(a) ? withoutNo(this.getSubstitutionText(a)) : this.getSubstitutionText(a)])
              .sort((a, b) => a[1].localeCompare(b[1]))
          );
        });
        this.orders = sortOrders(rawOrders);
      } finally {
        this.loading = false;
      }
    },
    combinedStream(order) {
      const base = order.streamName || '';
      const label = order.orderStreamName || '';
      if (label && label !== base) return `${base} (${label})`;
      return base;
    },
    clearFilters() {
      this.filterMeal = '';
      this.inputCustomer = '';
      this.inputBin = '';
      this.filterCustomer = '';
      this.filterBin = '';
      this.filterOrderStream = null;
      this.filterStream = null;
      this.filterSize = null;
      this.filterTod = null;
      this.filterPlatingLine = 'both';
    },
    toggleAll(val) {
      this.ordersFiltered.forEach(o => { o.selected = val; });
    },
    async downloadPDF() {
      const orders = this.selectedOrders;
      if (orders.length === 0) return;

      const useSticker = this.forceSticker || orders.some(o => o.meal && o.meal.print_option === 'sticker');
      this.generatingPDF = true;
      try {
        const payload = [];
        let lastMealId, index;
        for (const o of orders) {
          if (lastMealId !== o.meal.id) { index = 0; lastMealId = o.meal.id; }
          const streamName = o.streamName === 'chefs_choice' ? "Chef's Choice" : o.streamName;
          const instructions = (o.meal.instructions || []).map(i => i.trim()).filter(i => !!i);
          const restrictions = [];
          if (o.restrictionTagsText) {
            for (const [key, val] of o.restrictionTagsText.entries()) {
              restrictions.push({
                name: val,
                detected: !!(o.allergyDetected && o.allergyDetected[key]),
                contains: !!(o.allergyWarnings && o.allergyWarnings[key])
              });
            }
          }
          const day = getFullDay(o.order_date || o.date);
          const bestBefore = getBestBefore(o.order_date || o.date);
          const customerName = `${o.first_name} ${o.last_name}`;
          const filingId = `${(o.shipping_group || '?')[0].toUpperCase()}-${shortenBin(o.bin || '')}`;
          const qrcodeUrl = o.squid
            ? `https://fed.fyi/#/s/${o.squid}`
            : `https://fed.fyi/#/chart/fedfedfed/${o.meal_id}/${o.meal_size.substring(0, 1).toLowerCase()}`;

          if (useSticker) {
            const ingredientsStr = (o.meal.ingredients || []).map(ing => {
              const isRestricted = o.restrictedFoods && o.restrictedFoods[ing.food_id];
              return isRestricted ? `<span class="strike">${ing.name}</span>` : ing.name;
            }).join(', ');
            payload.push({
              day, bestBefore,
              size: capitalise(o.meal_size),
              tod: capitalise(o.order_tod || o.tod),
              stream: streamName,
              customerName,
              mealName: o.meal.name,
              ingredients: ingredientsStr,
              instructions,
              hasMultipleInstructions: instructions.length > 1,
              bin: filingId,
              showTruck: o.shipping_group !== 'local',
              pageNumber: `${String(o.meal.id).padStart(4, '0')}-${String(index + 1001).padStart(4, '0')}`,
              restrictions,
              qrcodeUrl,
              noTreeNuts: false
            });
          } else {
            const ingredients = (o.meal.ingredients || []).map(ing => ({
              isRestricted: o.restrictedFoods && o.restrictedFoods[ing.food_id],
              name: ing.name
            }));
            payload.push({
              day, bestBefore,
              size: capitalise(o.meal_size),
              tod: capitalise(o.order_tod || o.tod),
              stream: streamName,
              customerName,
              mealName: o.meal.name,
              ingredients,
              directions: instructions.join('<br/>'),
              labelId: `${String(o.meal.id).padStart(4, '0')}-${String(index + 1001).padStart(4, '0')}`,
              mealId: `M${o.meal.id}`,
              filingId,
              restrictions,
              qrcodeUrl,
              showTruck: o.shipping_group !== 'local'
            });
          }
          index++;
        }

        const requestBody = useSticker ? {stickers: payload} : payload;
        const response = await api.post('v2/label/pdf', requestBody, {responseType: 'blob'});
        const blob = new Blob([response.data], {type: response.headers['content-type']});
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `labels-${this.production}-${this.dates.join('-')}.pdf`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (e) {
        console.error('PDF generation failed', e);
        alert('Failed to generate PDF: ' + e.message);
      } finally {
        this.generatingPDF = false;
      }
    },
  },
  computed: {
    ...mapGetters(['getMeal', 'getStreams', 'getSubstitutionText']),
    streams() {
      return this.getStreams();
    },
    mealNameItems() {
      const seen = new Set();
      return this.orders
        .map(o => o.mealName)
        .filter(n => n && !seen.has(n) && seen.add(n))
        .sort();
    },
    orderStreamItems() {
      const streams = this.streams || {};
      const seen = new Set();
      return this.orders
        .map(o => o.order_stream)
        .filter(s => s && !seen.has(s) && seen.add(s))
        .sort()
        .map(s => ({value: s, text: (streams[s] || {name: s}).name}));
    },
    streamItems() {
      const streams = this.streams || {};
      const seen = new Set();
      return this.orders
        .map(o => o.stream)
        .filter(s => s && !seen.has(s) && seen.add(s))
        .sort()
        .map(s => ({value: s, text: (streams[s] || {name: s}).name}));
    },
    sizeItems() {
      const seen = new Set();
      return this.orders
        .map(o => o.meal_size)
        .filter(s => s && !seen.has(s) && seen.add(s))
        .sort()
        .map(s => ({value: s, text: capitalise(s)}));
    },
    todItems() {
      const todOrder = ['breakfast', 'lunch', 'dinner', 'extras'];
      const rank = t => { const i = todOrder.indexOf(t); return i === -1 ? todOrder.length : i; };
      const seen = new Set();
      return this.orders
        .map(o => o.order_tod || o.tod)
        .filter(t => t && !seen.has(t) && seen.add(t))
        .sort((a, b) => rank(a) - rank(b))
        .map(t => ({value: t, text: capitalise(t)}));
    },
    hasFilters() {
      return !!(this.filterMeal || this.filterCustomer || this.filterBin || this.filterOrderStream || this.filterStream || this.filterSize || this.filterTod);
    },
    ordersFiltered() {
      let result = this.orders;
      if (this.filterMeal) {
        const words = this.filterMeal.trim().split(/\s+/).map(w => new RegExp(w, 'i'));
        result = result.filter(o => o.meal && words.every(re => re.test(o.meal.name)));
      }
      if (this.filterCustomer) {
        const words = this.filterCustomer.trim().split(/\s+/).map(w => new RegExp(w, 'i'));
        result = result.filter(o => words.every(re => re.test(`${o.first_name} ${o.last_name}`)));
      }
      if (this.filterBin) {
        const re = new RegExp(this.filterBin, 'i');
        result = result.filter(o => re.test(o.bin || ''));
      }
      if (this.filterOrderStream) {
        result = result.filter(o => o.order_stream === this.filterOrderStream);
      }
      if (this.filterStream) {
        result = result.filter(o => o.stream === this.filterStream);
      }
      if (this.filterSize) {
        result = result.filter(o => o.meal_size === this.filterSize);
      }
      if (this.filterTod) {
        result = result.filter(o => (o.order_tod || o.tod) === this.filterTod);
      }
      if (this.filterPlatingLine === 'allergy') {
        result = result.filter(o => o.allergyDetected && Object.values(o.allergyDetected).some(v => v));
      } else if (this.filterPlatingLine === 'main') {
        result = result.filter(o => !o.allergyDetected || !Object.values(o.allergyDetected).some(v => v));
      }
      return result;
    },
    selectedOrders() {
      return this.ordersFiltered.filter(o => o.selected);
    },
    allSelected() {
      return this.ordersFiltered.length > 0 && this.ordersFiltered.every(o => o.selected);
    },
    someSelected() {
      return this.ordersFiltered.some(o => o.selected);
    },
    headers() {
      return [
        {value: 'selected', text: '', sortable: false, width: '48px'},
        {value: 'mealName', text: 'Meal'},
        {value: 'orderStreamName', text: 'Label Stream'},
        {value: 'streamName', text: 'Base Stream'},
        {value: 'customerName', text: 'Customer'},
        {value: 'meal_size', text: 'Size'},
        {value: 'bin', text: 'Bin'},
      ];
    },
  },
};
</script>

<style scoped>
.print-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}
.print-table th,
.print-table td {
  border: 1px solid #ccc;
  padding: 4px 6px;
  vertical-align: middle;
  white-space: nowrap;
}
.print-table th {
  background: #f5f5f5;
  text-align: left;
}
.print-col-check  { width: 27px; text-align: center; }
.print-col-bin    { width: 10%; }
.print-col-meal   { width: 32%; }
.print-col-stream { width: 13%; }
.print-col-customer { width: 22%; }
.print-col-size   { width: 8%; }
.print-checkbox {
  display: inline-block;
  width: 22px;
  height: 22px;
  border: 2px solid #000;
}
.print-ellipsis {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
@page {
  margin: 1cm;
}
@media print {
  .print-container {
    padding: 0 !important;
    margin: 0 !important;
  }
  .printing {
    padding: 0 !important;
    margin: 0 !important;
  }
  .print-filter-summary {
    margin-bottom: 6px;
  }
  .print-filter-table {
    width: 100%;
    //border-collapse: collapse;
    font-size: 10px;
  }
  .print-filter-label {
    color: #888;
    padding-right: 4px;
    white-space: nowrap;
    font-weight: 600;
    text-align: right;
  }
  .print-filter-value {
    padding-right: 12px;
    white-space: nowrap;
    text-align: left;
  }
  .print-sort-note {
    font-size: 9px;
    color: #aaa;
    margin-top: 3px;
    margin-bottom: 4px;
    font-style: italic;
  }
}
</style>
